import React, { useContext } from "react";
import R from "ramda";
import { defineMessages, useIntl } from "react-intl";
import { Button, Form } from "@natera/form";
import { DateRange, Timeslots } from "@natera/mobile-phlebotomy/lib";
import {
  TimezoneSelector,
  timezoneValidationName,
} from "@app/components/ui/inputs/timezoneSelector";
import { ErrorProvider, SpecificDatesContext } from "@app/provider";
import { getDisabledSlots, getLastSunday, getTimeZones } from "@app/utils";
import TimeslotsWarning from "../../sampleDraw/mobilePhlebotomy/timeslots/timeslotsWarning";
import { useErrorController } from "@natera/platform/lib/hooks";

import "@natera/mobile-phlebotomy/lib/theme/main.scss";
import {
  renderChipTime,
  renderSelectedSlot,
  setRemoveTimeslotAriaLabel,
} from "@app/utils/timeslotsUtils";

import "./timeslotsForm.scss";
import { addUtcDays, atUtcMidnight } from "@app/utils/convertDate";
import useExpectedDrawDates from "@app/hooks/useExpectedDrawDates";

const DAYS_AVAILABLE_FROM_GAP = 5;
const DAYS_AVAILABLE_TO_GAP_ONCOLOGY = 6;
const DAYS_AVAILABLE_TO_GAP = 15;
const DAYS_AROUND_PROSPERA_EBDD = 3;
// getDisabledSlots disables the availableTo day itself, so EBDD + 3 needs a gap of 4.
const DAYS_AVAILABLE_TO_GAP_PROSPERA = DAYS_AROUND_PROSPERA_EBDD + 1;
const MIN_BOOKABLE_DAYS = 3;

const messages = defineMessages({
  mobilePhlebotomyTimeslotsBack: {
    id: "mobilePhlebotomyTimeslotsBack",
    defaultMessage: "Back",
  },
  mobilePhlebotomyTimeslotsValidationMessage: {
    id: "mobilePhlebotomyTimeslotsValidationMessage",
    defaultMessage: "This information is required.",
  },
  mobilePhlebotomyTimeslotsPlaceholder: {
    id: "mobilePhlebotomy.timeslots.placeholder",
    defaultMessage: "Time slot #{number}",
  },
  mobilePhlebotomyTimeslotsTooltip: {
    id: "mobilePhlebotomy.timeslots.timeslotTooltip",
    defaultMessage: "You have selected maximum amount of timeslots",
  },
});

export type TimeslotsFormData = {
  timeSlots: { [x: string]: string };
  timeZone: string;
};

type Props = {
  handleConfirm: (data: TimeslotsFormData) => void;
  handleBack: () => void;
  confirmButtonLabel: string;
  isLoading?: boolean;
  isModify?: boolean;
};

const TimeslotsForm: React.FC<Props> = ({
  handleConfirm,
  handleBack,
  confirmButtonLabel,
  isLoading,
}) => {
  const intl = useIntl();
  const { isOncologyOrder, sampleCollectionDate, prosperaEbdd } =
    useExpectedDrawDates();

  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const now = new Date();
  const todayUtc = atUtcMidnight(now);

  const expectedDraw =
    isOncologyOrder && sampleCollectionDate
      ? {
          dateUtc: atUtcMidnight(new Date(sampleCollectionDate)),
          daysBefore: DAYS_AVAILABLE_FROM_GAP,
          daysAfter: DAYS_AVAILABLE_TO_GAP_ONCOLOGY,
        }
      : prosperaEbdd
        ? {
            dateUtc: atUtcMidnight(prosperaEbdd),
            daysBefore: DAYS_AROUND_PROSPERA_EBDD,
            daysAfter: DAYS_AVAILABLE_TO_GAP_PROSPERA,
          }
        : undefined;

  // Today + 5d @ 00:00Z
  const todayPlus5DaysUtc = atUtcMidnight(
    addUtcDays(todayUtc, DAYS_AVAILABLE_FROM_GAP)
  );

  let availableFrom = todayPlus5DaysUtc;

  if (expectedDraw) {
    const expectedDrawStartUtc = atUtcMidnight(
      addUtcDays(expectedDraw.dateUtc, -expectedDraw.daysBefore)
    );

    // Use whichever date is later
    if (expectedDrawStartUtc > todayPlus5DaysUtc) {
      availableFrom = expectedDrawStartUtc;
    }
  }

  let availableTo = expectedDraw
    ? atUtcMidnight(addUtcDays(expectedDraw.dateUtc, expectedDraw.daysAfter))
    : atUtcMidnight(addUtcDays(todayUtc, DAYS_AVAILABLE_TO_GAP));

  // Submitting needs slots on MIN_BOOKABLE_DAYS different days.
  const minAvailableTo = addUtcDays(availableFrom, MIN_BOOKABLE_DAYS);
  if (prosperaEbdd && availableTo < minAvailableTo) {
    availableTo = minAvailableTo;
  }

  const availableFromUtcDate = atUtcMidnight(availableFrom);

  const startWeek =
    availableFromUtcDate.getUTCDay() === 0
      ? availableFromUtcDate
      : getLastSunday(new Date(availableFromUtcDate));

  const endWeek = getLastSunday(new Date(availableTo));

  const [selectedSlots, setSelectedSlots] = React.useState<DateRange[]>([]);

  const selectedTimeZone = R.find(R.propEq("browserValue", browserTimeZone))(
    getTimeZones(intl)
  );
  const { specificDates } = useContext(SpecificDatesContext);

  const disabledSlots = getDisabledSlots(new Date(availableTo), specificDates);

  const [timeZone, setTimeZone] = React.useState<string | undefined>(
    selectedTimeZone?.browserValue
  );
  const [showNotification, setShowNotification] =
    React.useState<boolean>(false);
  const errorController = useErrorController();
  const timeslotsContainerRef = React.useRef<HTMLDivElement | null>(null);
  const selectedSlotsRef = React.useRef<DateRange[]>(selectedSlots);

  React.useEffect(() => {
    selectedSlotsRef.current = selectedSlots;
  }, [selectedSlots]);

  const selectedSlotsHandler = (value: DateRange[]) => {
    setSelectedSlots(value);
  };

  React.useEffect(() => {
    const chipsContainer = document.querySelector(
      ".mdc-evolution-chip-set__chips"
    );
    if (!chipsContainer) return;

    const removeAttributes = () => {
      document.querySelectorAll('span[role="gridcell"]').forEach((span) => {
        span.removeAttribute("role");
      });
      chipsContainer
        .querySelectorAll("[class*='placeholder-slot'][aria-hidden='true']")
        .forEach((el) => el.removeAttribute("aria-hidden"));
    };

    removeAttributes();

    const observer = new MutationObserver(removeAttributes);

    observer.observe(chipsContainer, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const timeslotsContainer = timeslotsContainerRef.current;
    if (!timeslotsContainer) return;

    const weekTitle = timeslotsContainer.querySelector(
      ".timeslots__week-title"
    );
    if (!weekTitle) return;

    const syncActiveDateWithVisibleWeek = () => {
      setTimeout(() => {
        const dayButtons = Array.from(
          timeslotsContainer.querySelectorAll<HTMLButtonElement>(
            'button.timeslots__day[role="radio"]'
          )
        );

        const firstAvailableDay = dayButtons.find(
          (button) =>
            !button.disabled && button.getAttribute("aria-disabled") !== "true"
        );

        const slots = selectedSlotsRef.current;
        const selectedSlotDaysOfMonth = new Set(
          slots.map((slot) => atUtcMidnight(slot.start).getUTCDate())
        );

        const dayOfMonthFromDayButton = (
          button: HTMLButtonElement
        ): number | null => {
          const rawDay =
            button.textContent?.trim() ??
            button.getAttribute("aria-label") ??
            "";
          const parsedDay = parseInt(rawDay, 10);
          if (!Number.isFinite(parsedDay) || parsedDay < 1 || parsedDay > 31)
            return null;
          return parsedDay;
        };

        let dayToClick = dayButtons.find((button) => {
          const domClickable =
            !button.disabled && button.getAttribute("aria-disabled") !== "true";
          if (!domClickable) return false;
          const buttonDayOfMonth = dayOfMonthFromDayButton(button);
          if (buttonDayOfMonth === null) return false;
          return !selectedSlotDaysOfMonth.has(buttonDayOfMonth);
        });

        if (
          dayToClick === undefined &&
          selectedSlotDaysOfMonth.size === 0 &&
          firstAvailableDay
        ) {
          dayToClick = firstAvailableDay;
        }

        if (dayToClick && dayToClick.getAttribute("aria-checked") !== "true") {
          dayToClick.click();
        }
      }, 0);
    };

    const observer = new MutationObserver(() => {
      syncActiveDateWithVisibleWeek();
    });

    observer.observe(weekTitle, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, []);

  const onConfirm: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    setShowNotification(false);
    errorController.clearValidationError(timezoneValidationName);

    const minNumberOfSelectedSlots = 3;

    if (!timeZone) {
      errorController.setValidationError(
        timezoneValidationName,
        intl.formatMessage(messages.mobilePhlebotomyTimeslotsValidationMessage)
      );

      return;
    }

    if (selectedSlots.length < minNumberOfSelectedSlots) {
      setShowNotification(true);
    }

    if (timeZone && selectedSlots.length >= minNumberOfSelectedSlots) {
      const formattedTimeSlots = R.mergeAll(
        selectedSlots.map((timeSlot, index) => ({
          [`start${index + 1}`]: timeSlot["start"].toISOString(),
          [`end${index + 1}`]: timeSlot["end"].toISOString(),
        }))
      );

      handleConfirm({
        timeSlots: formattedTimeSlots,
        timeZone: timeZone,
      });
    }
  };

  const handleTimezoneSelect = (timezone: string) => {
    errorController.clearValidationError(timezoneValidationName);
    setTimeZone(timezone);
  };

  const setRemoveTimeslotAriaLabelForm = (slot: DateRange) => {
    return setRemoveTimeslotAriaLabel(slot, intl);
  };

  return (
    <Form
      onSubmit={onConfirm}
      noValidate
      buttons={
        <>
          <Button
            type="submit"
            unelevated
            loading={isLoading}
            data-testid="submit"
          >
            {!isLoading && confirmButtonLabel}
          </Button>
          <Button type="reset" data-testid="cancel" onClick={handleBack}>
            {intl.formatMessage(messages.mobilePhlebotomyTimeslotsBack)}
          </Button>
        </>
      }
    >
      <div
        className="timeslots_container"
        data-testid="timeslots_container"
        ref={timeslotsContainerRef}
      >
        <ErrorProvider controller={errorController}>
          <TimezoneSelector value={timeZone} onSelect={handleTimezoneSelect} />
        </ErrorProvider>
        {showNotification && <TimeslotsWarning />}
        <Timeslots
          onChange={selectedSlotsHandler}
          startWeek={startWeek}
          currentDate={now}
          selectedSlots={selectedSlots}
          showSlotPlaceholder
          availableFrom={availableFrom}
          endWeek={endWeek}
          disabledSlots={disabledSlots}
          mobile
          oneSlotPerDay
          renderSelectedSlotTime={renderSelectedSlot}
          renderSlotChipTime={renderChipTime}
          setRemoveTimeslotAriaLabel={setRemoveTimeslotAriaLabelForm}
          data-testid="timeslots_selector"
        />
      </div>
    </Form>
  );
};

export default TimeslotsForm;
